<template>
    <Dialog :visible="visible" @update:visible="updateVisible" :header="`Acessos de ${usuario.nome || ''}`"
        @show="onShow" @hide="onHide" class="w-11 lg:w-6" modal>

        <p class="mt-0 mb-3 text-sm text-color-secondary">
            Cada empresa é concedida junto com o perfil que o usuário terá nela.
        </p>

        <div v-if="!isLoading && !empresas.length" class="p-3 border-round surface-100 text-sm">
            Você não é Admin de nenhuma empresa, por isso não há acesso a conceder aqui.
            Peça a um Admin da empresa desejada.
        </div>

        <form v-else class="mb-4" @submit.prevent="conceder">
            <div class="flex flex-column md:flex-row gap-3">
                <div class="field flex-1 mt-4 mb-0">
                    <FloatLabel>
                        <Select id="acesso_tenant" ref="selectEmpresa" v-model="form.tenant_id" :options="empresas" optionLabel="nome"
                            optionValue="id" class="w-full" :disabled="isBusy" @change="form.perfil_id = null" />
                        <label for="acesso_tenant">Empresa<span class="text-red-500">*</span></label>
                    </FloatLabel>
                    <div class="text-xs text-red-500">{{ errors['tenant_id'] }}</div>
                </div>
                <div class="field flex-1 mt-4 mb-0">
                    <FloatLabel>
                        <Select id="acesso_perfil" ref="selectPerfil" v-model="form.perfil_id" :options="perfisDaEmpresa"
                            optionLabel="descricao" optionValue="id" class="w-full"
                            :disabled="isBusy || !form.tenant_id" />
                        <label for="acesso_perfil">Perfil<span class="text-red-500">*</span></label>
                    </FloatLabel>
                    <div class="text-xs text-red-500">{{ errors['perfil_id'] }}</div>
                </div>
                <div class="flex align-items-center">
                    <Button type="submit" label="Conceder" icon="pi pi-check" severity="success" size="small"
                        class="w-full md:w-auto" :loading="isSaving" :disabled="!form.tenant_id || !form.perfil_id" />
                </div>
            </div>
        </form>

        <p class="text-sm text-color-secondary" role="status" aria-live="polite">{{ situacao }}</p>

        <DataTable :value="linhas" :loading="isLoading" size="small" stripedRows showGridlines>
            <Column field="empresa" header="Empresa"></Column>
            <Column header="Perfil">
                <template #body="{ data }">
                    <span v-if="data.perfil">{{ data.perfil }}</span>
                    <Tag v-else severity="warn" icon="pi pi-exclamation-triangle" value="Sem perfil"
                        v-tooltip.top="'Vínculo antigo: entra na empresa mas não faz nada. Conceda um perfil para corrigir.'" />
                </template>
            </Column>
            <Column style="width: 160px" bodyClass="text-right white-space-nowrap">
                <template #body="{ data }">
                    <Button v-if="!data.perfil" label="Definir perfil" size="small" severity="warn" text
                        :disabled="isBusy" @click="definirPerfil(data)" />
                    <Button icon="pi pi-trash" severity="danger" text rounded size="small" :disabled="isBusy"
                        :aria-label="`Revogar acesso à empresa ${data.empresa}`" v-tooltip.top="'Revogar acesso'"
                        @click="confirmarRevogar(data)" />
                </template>
            </Column>
            <template #empty>
                <!--
                    `v-if="!isLoading"`: o `#empty` do DataTable renderiza
                    sempre que a lista está vazia, e durante o carregamento ela
                    está — por ainda não ter chegado. Sem a guarda, o diálogo
                    abria afirmando "sem acesso a nenhuma empresa" por cima do
                    próprio spinner, e quem lesse rápido levava a informação
                    errada embora.
                -->
                <div v-if="!isLoading" class="text-center text-color-secondary">
                    Sem acesso a nenhuma das empresas que você administra.
                </div>
            </template>
        </DataTable>

        <div class="flex justify-content-end gap-1 mt-3">
            <Button label="Fechar" severity="warn" icon="pi pi-times" size="small" :disabled="isBusy"
                @click="updateVisible(false)" />
        </div>
    </Dialog>
</template>
<script>
import Dialog from 'primevue/dialog';
import FloatLabel from 'primevue/floatlabel';
import Select from 'primevue/select';
import Button from 'primevue/button';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Tag from 'primevue/tag';

import Usuario from '@/service/usuario.js';

export default {
    components: { Dialog, FloatLabel, Select, Button, DataTable, Column, Tag },
    props: {
        usuario: Object,
        visible: { type: Boolean, default: false },
    },
    emits: ['save', 'close'],
    data() {
        return {
            acessos: [],
            empresas: [],
            form: { tenant_id: null, perfil_id: null },
            errors: {},
            isLoading: false,
            isSaving: false,
        }
    },
    computed: {
        isBusy() {
            return this.isLoading || this.isSaving;
        },
        perfisDaEmpresa() {
            return this.empresas.find(e => e.id === this.form.tenant_id)?.perfis ?? [];
        },
        // Rótulo mora só em `empresas` — as linhas casam por id, nunca por nome.
        linhas() {
            return this.acessos.map(acesso => {
                const empresa = this.empresas.find(e => e.id === acesso.tenant_id);
                return {
                    tenant_id: acesso.tenant_id,
                    empresa: empresa?.nome ?? `Empresa #${acesso.tenant_id}`,
                    perfil: empresa?.perfis.find(p => p.id === acesso.perfil_id)?.descricao ?? null,
                };
            });
        },
        situacao() {
            if (this.isLoading) return 'Carregando acessos…';
            if (this.isSaving) return 'Salvando…';
            return `${this.linhas.length} empresa(s) com acesso.`;
        },
    },
    methods: {
        onShow() {
            this.errors = {};
            this.form = { tenant_id: null, perfil_id: null };
            this.acessos = [];
            this.empresas = [];
            this.isLoading = true;
            Usuario.acessos(this.usuario.id)
                .then(this.aplicar)
                .catch(this.reportar)
                .finally(() => { this.isLoading = false });
        },
        onHide() {
            this.acessos = [];
            this.empresas = [];
        },
        updateVisible(visible) {
            this.$emit('close', visible);
        },
        // Os três endpoints devolvem o estado pós-operação: refetch depois de
        // escrever perderia o usuário quando o ator revoga a empresa ativa.
        aplicar(response) {
            this.acessos = response.data.data.acessos;
            this.empresas = response.data.data.empresas;
            if (response.data.msg) {
                this.$toast.add({ severity: 'success', summary: 'Sucesso', detail: response.data.msg, life: 4000 });
            }
        },
        conceder() {
            this.errors = {};
            this.isSaving = true;
            Usuario.conceder(this.usuario.id, this.form)
                .then(response => {
                    this.aplicar(response);
                    this.form = { tenant_id: null, perfil_id: null };
                    this.$emit('save');
                })
                .catch(this.reportar)
                .finally(() => { this.isSaving = false });
        },
        definirPerfil(linha) {
            this.form = { tenant_id: linha.tenant_id, perfil_id: null };
            this.focar('selectPerfil');
        },
        // O Select do PrimeVue não expõe focus(); o elemento focável é o
        // combobox interno.
        focar(ref) {
            this.$nextTick(() => this.$refs[ref]?.$el?.querySelector('[role="combobox"]')?.focus());
        },
        confirmarRevogar(linha) {
            this.$confirm.require({
                header: 'Revogar acesso',
                message: `Revogar o acesso de ${this.usuario.nome} à empresa ${linha.empresa}? `
                    + (linha.perfil
                        ? `O perfil ${linha.perfil} nessa empresa sai junto.`
                        : 'O vínculo sai por inteiro — para só corrigir o perfil, use "Definir perfil".'),
                icon: 'pi pi-trash',
                acceptLabel: 'Revogar',
                acceptClass: 'bg-red-500 text-white border-red-500',
                rejectLabel: 'Cancelar',
                rejectClass: 'bg-red-400 text-white',
                accept: () => {
                    this.isSaving = true;
                    Usuario.revogar(this.usuario.id, linha.tenant_id)
                        .then(response => {
                            this.aplicar(response);
                            this.$emit('save');
                            // A linha (e o botão que abriu a confirmação) some
                            // com o sucesso: sem isto o foco cai no body.
                            this.focar('selectEmpresa');
                        })
                        .catch(this.reportar)
                        .finally(() => { this.isSaving = false });
                },
            });
        },
        // Dois formatos: guarda (403/422) manda {message}; validação de campo
        // manda {msg, erros}. A mensagem do servidor é a específica — use ela.
        reportar(error) {
            const data = error.response?.data;
            for (const campo in (data?.erros ?? {})) {
                this.errors[campo] = data.erros[campo][0];
            }
            this.$toast.add({
                severity: 'error',
                summary: 'Ops!',
                detail: data?.message || data?.msg || error.message,
                life: 6000,
            });
        },
    },
}
</script>
